import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import PageHero from '@/components/base/PageHero';

export default function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Use — The Great Multitude Church International';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <PageHero
          title="Terms of Use"
          subtitle="Please read these terms carefully before using our website and services."
          breadcrumb="Terms of Use"
          slot="terms_hero"
        />

        <section className="py-16 md:py-24">
          <div className="max-w-[820px] mx-auto px-4 md:px-8">
            <p className="text-foreground-500 text-sm mb-10">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="space-y-10 text-foreground-700 leading-relaxed text-base">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">1. Acceptance of Terms</h2>
                <p>
                  By accessing or using the website of The Great Multitude Church International (&ldquo;TGMCI,&rdquo;
                  &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), you agree to be bound by these Terms of Use. If you
                  do not agree with any part of these terms, please do not use our website.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">2. Use of the Website</h2>
                <p className="mb-3">You agree to use our website only for lawful purposes and in a manner that does not:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Violate any applicable local, national, or international law or regulation.</li>
                  <li>Infringe the rights of others or attempt to gain unauthorized access to our systems.</li>
                  <li>Transmit any harmful, unlawful, or objectionable content.</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">3. Intellectual Property</h2>
                <p>
                  All content on this website, including text, graphics, logos, images, and software, is the property of TGMCI
                  or its content suppliers and is protected by applicable intellectual property laws. You may not reproduce,
                  distribute, or create derivative works from any content without our prior written permission.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">4. Donations and Giving</h2>
                <p>
                  Donations made through the PureHeart Africa program are voluntary and, unless otherwise stated, are
                  processed by our team. We make every effort to ensure donations are used in accordance with our stated
                  mission. Donation inquiries submitted through our website are not a binding contract until confirmed by us.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">5. Third-Party Links</h2>
                <p>
                  Our website may contain links to third-party websites, including the official PureHeart International
                  website. We are not responsible for the content, accuracy, or practices of any third-party sites. Accessing
                  such sites is at your own risk.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">6. Disclaimer of Warranties</h2>
                <p>
                  This website is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. We make no
                  warranties, express or implied, regarding the operation or availability of the website or the accuracy,
                  completeness, or reliability of its content.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">7. Limitation of Liability</h2>
                <p>
                  To the fullest extent permitted by law, TGMCI shall not be liable for any indirect, incidental, special, or
                  consequential damages arising out of or in connection with your use of this website.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">8. Changes to These Terms</h2>
                <p>
                  We may update these Terms of Use from time to time. Any changes will be posted on this page with an updated
                  &ldquo;Last updated&rdquo; date. Your continued use of the website after such changes constitutes acceptance
                  of the revised terms.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">9. Governing Law</h2>
                <p>
                  These Terms of Use shall be governed by and construed in accordance with the laws of the Republic of Ghana
                  (where our head office is located) and the Republic of Cameroon (where our Cameroon branch operates),
                  without regard to conflict of law principles.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">10. Contact Us</h2>
                <p className="mb-3">
                  If you have any questions about these Terms of Use, please contact us using any of the details below:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-3">
                  <li>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:info@tgmci.org" className="text-primary-600 hover:text-primary-700 font-medium">info@tgmci.org</a>{' '}
                    or{' '}
                    <a href="mailto:donations@tgmci.org" className="text-primary-600 hover:text-primary-700 font-medium">donations@tgmci.org</a>
                  </li>
                  <li><strong>Head Office:</strong> Dansoman, Accra, Ghana</li>
                  <li><strong>Cameroon Branch:</strong> Mbanda Sic. 865, Edea, Cameroon</li>
                  <li><strong>Phone:</strong> +237 653 760 724 (Cameroon) or +1 623 800 9241 (USA)</li>
                </ul>
                <p>
                  You may also reach us through our <Link to="/contact" className="text-primary-600 hover:text-primary-700 font-medium">contact page</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}